/**
 * Funil: board e oportunidade (D04, §6, §10.3, §11).
 *
 * ── O board é por ETAPA, não uma lista com campo de etapa ─────────────────
 *
 * A tela é Kanban (H09): colunas com contagem e valor somado no cabeçalho, e
 * os cards dentro. Devolver lista achatada obrigaria o cliente a agrupar e a
 * somar — e duas telas fariam isso de dois jeitos, com dois arredondamentos.
 *
 * Cada coluna traz `total` e `valor_total_micros` do CONJUNTO, não da fatia
 * carregada: é o número do cabeçalho, e um número que muda conforme se rola a
 * coluna não serve para decidir nada.
 *
 * ── `version` no movimento não é opcional ─────────────────────────────────
 *
 * A §10.3 exige, e a razão é concreta: dois vendedores arrastando o mesmo card
 * sem isso produz última-escrita-vence, e um perde a alteração sem saber. O
 * valor é o `atualizadaEm` que veio na leitura — quem move devolve o que leu.
 *
 * ── `risco` vem com o MOTIVO, não só a cor ────────────────────────────────
 *
 * "Sem interação" e "etapa parada" pedem conversas diferentes do gestor com o
 * vendedor. Um selo vermelho sem motivo obriga a abrir o card para descobrir
 * qual dos dois é — e aí o indicador não economizou nada.
 */

import { z } from 'zod';
import { MudancaDeCamposSchema, ValoresDeCamposSchema } from './campos-personalizados.js';
import { IdSchema } from './comum.js';
import { InstanteSchema } from './valores.js';
import { PerdaDaOportunidadeSchema } from './motivos-de-perda.js';

export const SinalDeRiscoSchema = z.enum(['sem_interacao', 'etapa_parada']);

export const RiscoSchema = z.object({
  em_risco: z.boolean(),
  sinais: z.array(SinalDeRiscoSchema),
  dias_sem_interacao: z.number().int().nullable(),
  dias_na_etapa: z.number().int().nullable(),
});

export const CardDoFunilSchema = z.object({
  /** Canônico quando já cunhado (C13); `null` enquanto só a engine conhece. */
  id: IdSchema.nullable(),
  referencia_externa: z.string(),
  titulo: z.string().nullable(),
  etapa: z.string(),
  valor: z.object({ quantia_micros: z.number(), moeda: z.string() }).nullable(),
  /**
   * O que o `PATCH` exige de volta. É o `updatedAt` da engine — opaco de
   * propósito: quem move devolve o que leu, sem interpretar.
   */
  version: z.string(),
  cliente: z.object({ id: IdSchema.nullable(), nome_exibicao: z.string().nullable() }).nullable(),
  /** Os campos personalizados da oportunidade (#568), pela cópia do funil. */
  campos: ValoresDeCamposSchema,
  /**
   * De quem é esta negociação — o id do membro no CRM.
   *
   * Referência externa e não nome: o board sai da projeção, e a projeção
   * guarda o id. Traduzir para nome exigiria cruzar a equipe inteira a cada
   * carga do funil, para um dado que a tela usa apenas ao abrir a atribuição.
   *
   * `null` é card sem dono — e num funil isso é pendência, não neutralidade.
   */
  responsavel_referencia_externa: z.string().nullable(),
  /**
   * Quem é o responsável, para o avatar no canto do card (28/09/2026): o nome
   * e a foto de perfil (061). Nulo quando o card não tem dono.
   *
   * `nome` nulo com o objeto presente é o membro do CRM sem vínculo nesta
   * organização — existe dono, e a tela não sabe dizer quem.
   */
  responsavel: z
    .object({ nome: z.string().nullable(), foto_url: z.string().nullable() })
    .nullable(),
  /** Atalho para abrir a conversa direto do card (§11). */
  conversa_id: IdSchema.nullable(),
  /**
   * Por onde o cliente fala: o canal da caixa dessa conversa (`whatsapp_cloud`,
   * `instagram`…). A tela escreve o nome. Opcional pela régua do `/v1`.
   */
  canal: z.string().nullable().optional(),
  /**
   * O que fazer agora: a próxima tarefa em aberto da oportunidade, a de prazo
   * mais próximo (sem prazo, por último). Opcional pela régua do `/v1`.
   */
  proxima_tarefa: z
    .object({ titulo: z.string(), prazo_em: InstanteSchema.nullable() })
    .nullable()
    .optional(),
  ultima_interacao_em: InstanteSchema.nullable(),
  /** Desde quando está NESTA etapa — o insumo do risco de etapa parada. */
  etapa_desde_em: InstanteSchema.nullable(),
  fechamento_previsto_em: InstanteSchema.nullable(),
  risco: RiscoSchema,
  /** A seguinte no funil da revenda, para a tela oferecer o movimento óbvio. */
  proxima_etapa: z.string().nullable(),
});

/**
 * Passar a oportunidade para outro vendedor.
 *
 * `usuario_id` nulo desatribui — e aí a CONVERSA fica como está: "ninguém está
 * conduzindo esta negociação" é diferente de "ninguém está atendendo esta
 * pessoa agora", e arrastar a conversa junto tiraria da fila de quem está no
 * meio de uma resposta.
 */
export const AtribuirOportunidadeSchema = z.object({
  usuario_id: IdSchema.nullable(),
  /**
   * Por que passou. Obrigatório, como na transferência de conversa: a
   * atribuição entra na trilha, e "mudou de dono" sem motivo não responde a
   * ninguém que for olhar depois.
   */
  motivo: z.string().trim().min(3).max(500),
});

/**
 * O que moveu, item a item.
 *
 * `conversas` é um NÚMERO e não um booleano: uma oportunidade pode ter mais de
 * uma conversa ligada, e "moveu" esconderia que só três das quatro andaram.
 */
export const AtribuicaoDaOportunidadeSchema = z.object({
  responsavel: z.object({ usuario_id: IdSchema, nome: z.string() }).nullable(),
  moveu: z.object({ oportunidade: z.boolean(), conversas: z.number().int() }),
});

export type CardDoFunil = z.infer<typeof CardDoFunilSchema>;

/**
 * UMA oportunidade, com o mesmo card do quadro, e as etapas do funil dela
 * (28/09/2026) — para o Cliente 360 abrir o negócio sem carregar o quadro.
 *
 * As etapas vêm porque o card guarda a etapa pelo VALOR, e a tela precisa do
 * nome dela e da ordem para dizer qual é a próxima.
 */
export const OportunidadeLidaSchema = z.object({
  card: CardDoFunilSchema,
  etapas: z.array(z.object({ chave: z.string(), nome: z.string() })),
});

export type OportunidadeLida = z.infer<typeof OportunidadeLidaSchema>;

export const ColunaDoFunilSchema = z.object({
  etapa: z.string(),
  rotulo: z.string(),
  posicao: z.number().int(),
  /** `false` = etapa terminal; a tela não oferece arrastar para fora dela. */
  aberta: z.boolean(),
  /**
   * Ganho ou perdido, na coluna final classificada (057). `perdido` é o que faz
   * a tela perguntar o motivo ao levar um negócio para cá (058).
   */
  resultado: z.enum(['ganho', 'perdido']).nullable(),
  /** Do CONJUNTO da coluna, não da fatia carregada. */
  total: z.number().int(),
  valor_total_micros: z.number(),
  cards: z.array(CardDoFunilSchema),
  tem_mais: z.boolean(),
});

/**
 * Um funil da revenda.
 *
 * ── `chave` é o prefixo da etapa, e isso é contrato ──────────────────────
 *
 * As etapas de um funil valem `F_CHAVE_ETAPA` no motor — `F_VENDAS_NOVO`. A
 * chave `padrao` é a exceção: as etapas dele não têm prefixo, porque são as que
 * já existiam antes de haver mais de um funil. Ver `packages/modulos/comercial/src/dominio/funil.ts`.
 */
export const FunilSchema = z.object({
  chave: z.string(),
  /**
   * O id do funil, que a tela mostra ("Funil - Pós venda - 2").
   *
   * Fixo: o padrão é 1, os outros recebem um número na primeira vez que
   * aparecem, e um número nunca é reaproveitado. A chave continua sendo o que
   * identifica o funil nas rotas; o número é para gente.
   */
  numero: z.number().int().positive(),
  nome: z.string(),
  /** Quantas etapas ele tem. Um funil sem etapa não existe — ver o servidor. */
  etapas: z.number().int().nonnegative(),
  /**
   * O quadro tem o botão "Atualizar" (#1136): o Super Admin marcou ao menos uma
   * rotina para ele adiantar. Sem isso, o botão não aparece.
   */
  com_atualizar: z.boolean(),
});

export type Funil = z.infer<typeof FunilSchema>;

export const FunisSchema = z.object({ itens: z.array(FunilSchema) });

export type Funis = z.infer<typeof FunisSchema>;

/** A mesma forma do valor de etapa: é disso que a chave vira prefixo. */
const CHAVE_DE_FUNIL = /^[A-Z][A-Z0-9]*(?:_[A-Z0-9]+)*$/;

/**
 * Criar um funil é criar a PRIMEIRA ETAPA dele.
 *
 * Não existe funil vazio: ele é o conjunto das etapas com o prefixo dele, então
 * um sem etapa nenhuma não teria quadro para mostrar nem para onde arrastar —
 * seria um estado que alguém teria de limpar depois, e que a tela teria de
 * explicar.
 *
 * Por isso o pedido traz as duas coisas: o nome do funil e a etapa com que ele
 * começa. É também o que impede o meio-caminho — funil criado, etapa falhando,
 * e a revenda com um funil fantasma na lista.
 */
export const CriarFunilSchema = z
  .object({
    chave: z
      .string()
      .trim()
      .min(2)
      .max(30)
      .regex(
        CHAVE_DE_FUNIL,
        'Use MAIÚSCULAS e sublinhado, como POS_VENDA — é a forma que o motor aceita.',
      ),
    /** O que se lê na tela. Aqui cabe acento e espaço, ao contrário da chave. */
    nome: z.string().trim().min(1).max(40),
    primeira_etapa: z.object({
      /** O valor SEM o prefixo: quem compõe o valor completo é o servidor. */
      valor: z
        .string()
        .trim()
        .min(2)
        .max(40)
        .regex(CHAVE_DE_FUNIL, 'Use MAIÚSCULAS e sublinhado, como PRIMEIRO_CONTATO.'),
      rotulo: z.string().trim().min(1).max(40),
      cor: z.string().min(1),
    }),
  })
  .strict();

export type CriarFunil = z.infer<typeof CriarFunilSchema>;

/**
 * Renomear muda só o NOME.
 *
 * A chave é imutável pela mesma razão que o valor da etapa é: ela está escrita
 * em toda oportunidade do funil, dentro do valor da etapa. Trocá-la exigiria
 * reescrever todas — e uma falha no meio deixaria metade da revenda num funil
 * que não existe mais.
 */
export const RenomearFunilSchema = z.object({ nome: z.string().trim().min(1).max(40) }).strict();

export type RenomearFunil = z.infer<typeof RenomearFunilSchema>;

export const BoardDoFunilSchema = z.object({
  /*
   * QUAL funil este quadro mostra.
   *
   * Vem na resposta porque o pedido pode não ter dito — sem `{id}`, o servidor
   * devolve o padrão —, e a tela precisa saber qual aba destacar sem adivinhar.
   */
  funil: FunilSchema,
  colunas: z.array(ColunaDoFunilSchema),
  frescor: z.object({
    stale: z.boolean(),
    projetado_em: InstanteSchema.nullable(),
  }),
});

export type BoardDoFunil = z.infer<typeof BoardDoFunilSchema>;

/**
 * O "Atualizar" do Funil (28/10): o quadro relido e as rotinas agendadas que
 * alimentam o funil adiantadas. `disparadas` conta as rotinas que entraram na
 * fila agora; `proxima_em` é quando o botão volta a disparar (a trava é por
 * funil, para a organização toda).
 */
export const AtualizacaoDoFunilSchema = z.object({
  disparadas: z.number().int().min(0),
  proxima_em: z.string().datetime(),
});

export type AtualizacaoDoFunil = z.infer<typeof AtualizacaoDoFunilSchema>;

/** A trava do "Atualizar": uma vez a cada tanto, por funil, para a organização toda. */
export const JANELA_DO_ATUALIZAR_EM_SEGUNDOS = 120;

/**
 * As rotinas que o "Atualizar" de um funil pode adiantar, para o Super Admin
 * escolher (#1136). Só aparecem as que rodariam de novo sem efeito em dobro:
 * ativas, de agenda, do modelo de rotina, criando card com referência ou
 * movendo pela referência, numa etapa deste funil. `marcada` diz se o botão a
 * adianta hoje.
 */
export const ConfiguracaoDoAtualizarSchema = z.object({
  rotinas: z.array(
    z.object({
      id: z.string(),
      nome: z.string().nullable(),
      marcada: z.boolean(),
      /**
       * Pode rodar pelo botão agora. `false` só aparece numa marcada que deixou
       * de servir (pausada, mudou de ação, foi apagada): ela continua na lista,
       * para ninguém a desmarcar sem ver, e não roda enquanto não voltar.
       */
      disponivel: z.boolean(),
    }),
  ),
});

export type ConfiguracaoDoAtualizar = z.infer<typeof ConfiguracaoDoAtualizarSchema>;

/**
 * As rotinas que o botão passa a adiantar. Lista vazia tira o botão do funil.
 * Uma marcada que deixou de servir pode continuar na lista; uma nova, não.
 */
export const DefinirConfiguracaoDoAtualizarSchema = z
  .object({ rotinas: z.array(z.string().min(1).max(64)).max(20) })
  .strict();

export type DefinirConfiguracaoDoAtualizar = z.infer<typeof DefinirConfiguracaoDoAtualizarSchema>;

export const CriarOportunidadeSchema = z.object({
  nome: z.string().min(1).max(500),
  /** Ausente = a primeira etapa do funil da revenda. */
  etapa: z.string().nullish(),
  valor: z
    .object({ quantia_micros: z.number().int().nonnegative(), moeda: z.string().length(3) })
    .nullish(),
  /** Canônico do cliente (§5.1) — nunca o id da engine. */
  cliente_id: IdSchema.nullish(),
  /*
   * Em qual funil ela nasce. Ausente é o padrão.
   *
   * Só vale quando a `etapa` não vem: a etapa já carrega o funil dentro do
   * valor (`F_VENDAS_NOVO`), e mandar os dois abriria o pedido contraditório
   * — funil de Vendas com etapa de Oficina —, que não tem resposta certa.
   */
  funil: z.string().nullish(),
  fechamento_previsto_em: InstanteSchema.nullish(),
  responsavel_referencia_externa: z.string().nullish(),
  /*
   * As notas escritas ANTES de a oportunidade existir.
   *
   * Quem cria o negócio acabou de desligar o telefone, e o que foi combinado
   * está fresco. Obrigar a criar primeiro e escrever depois faria a segunda
   * metade depender de a pessoa voltar ao card — e o combinado ficaria de fora
   * justamente no caso em que ela tem a informação na cabeça.
   *
   * Chegam no MESMO pedido para não existir o desfecho em que a oportunidade
   * nasce e as notas se perdem por falha de rede entre uma chamada e a outra.
   */
  notas: z.array(z.string().trim().min(1).max(2000)).max(20).optional(),
  /** Os campos personalizados preenchidos ao criar (#568). */
  campos: MudancaDeCamposSchema.optional(),
});

export type CriarOportunidade = z.infer<typeof CriarOportunidadeSchema>;

export const MoverEtapaSchema = z.object({
  etapa: z.string().min(1),
  /**
   * O `version` que veio no card. Obrigatório: sem ele, dois vendedores
   * movendo o mesmo card produzem última-escrita-vence (§10.3).
   */
  version: z.string().min(1),
  /** O motivo, quando a etapa de destino é de perda (058). */
  perda: PerdaDaOportunidadeSchema.optional(),
});

export type MoverEtapa = z.infer<typeof MoverEtapaSchema>;

/**
 * O corpo do `PATCH` da oportunidade — mover de etapa OU editar os dados.
 *
 * ── Por que um PATCH só, e não uma rota de edição ao lado ─────────────────
 *
 * É o mesmo recurso. Duas rotas para editar a mesma oportunidade dariam duas
 * portas com regras diferentes, e a pergunta "qual eu uso?" se responderia
 * lendo código. Aqui o corpo diz o que se quer mudar, e cada campo carrega a
 * sua regra.
 *
 * ── Mover e editar no MESMO pedido é recusado ─────────────────────────────
 *
 * Não por preguiça: são duas escritas na engine, e a segunda pode falhar
 * depois de a primeira ter valido. O resultado seria um card em outra etapa
 * com o valor antigo, e nada na tela dizendo qual metade passou.
 *
 * Mover é um gesto (arrastar, ou escolher a etapa) e editar é outro (abrir o
 * formulário). Separados no contrato, ficam separados na falha também.
 *
 * ── Ausente é "não mexer"; `null` apaga ──────────────────────────────────
 *
 * Mesma convenção do cadastro do cliente (`AtualizarClienteSchema`), e pela
 * mesma razão: sem ela, um formulário que não mostra um campo o apagaria ao
 * salvar. `titulo` é a exceção — não aceita `null`, porque negócio sem nome
 * não é um estado que a criação permita alcançar.
 *
 * ── `version` continua obrigatório, inclusive para editar ────────────────
 *
 * A §10.3 o exige para mover, para dois vendedores não produzirem
 * última-escrita-vence. Editar tem o mesmo problema e a mesma resposta: quem
 * salva o valor com o card carregado há dez minutos precisa saber que alguém
 * mexeu antes, e não descobrir pelo total da coluna.
 */
export const EditarOportunidadeSchema = z
  .object({
    /** Presente = mover. A transição é validada como regra de produto. */
    etapa: z.string().min(1).optional(),
    titulo: z.string().trim().min(1).max(500).optional(),
    valor: z
      .object({ quantia_micros: z.number().int().nonnegative(), moeda: z.string().length(3) })
      .nullish(),
    fechamento_previsto_em: InstanteSchema.nullish(),
    /** Canônico do cliente (§5.1) — nunca o id da engine. `null` desvincula. */
    cliente_id: IdSchema.nullish(),
    version: z.string().min(1),
    /**
     * Só com `etapa`, e só quando ela é de PERDA: o motivo e a observação
     * (058). Levar para a perda sem ele é recusado quando a revenda tem motivo
     * cadastrado.
     */
    perda: PerdaDaOportunidadeSchema.optional(),
    /**
     * Só os campos personalizados que mudaram (#568); `null` esvazia. Mover
     * para uma etapa que exige um campo vazio é recusado com 400, com cada
     * campo que falta em `erros` (`campos.<id>`): a tela pede, salva e move.
     */
    campos: MudancaDeCamposSchema.optional(),
  })
  .strict()
  .refine((corpo) => corpo.perda === undefined || corpo.etapa !== undefined, {
    message: 'O motivo da perda vai junto com a mudança de etapa, e não sozinho.',
  })
  .refine((corpo) => Object.keys(corpo).some((campo) => campo !== 'version'), {
    message: 'Diga o que mudar: só `version` não descreve nenhuma mudança.',
  })
  .refine(
    (corpo) =>
      corpo.etapa === undefined ||
      ['titulo', 'valor', 'fechamento_previsto_em', 'cliente_id', 'campos'].every(
        (campo) => !(campo in corpo),
      ),
    {
      message:
        'Mover de etapa e editar os dados são dois pedidos. Juntos, uma falha na segunda ' +
        'escrita deixaria o card em outra etapa com os dados antigos.',
    },
  );

export type EditarOportunidade = z.infer<typeof EditarOportunidadeSchema>;

export const OportunidadeSchema = z.object({
  id: IdSchema.nullable(),
  referencia_externa: z.string(),
  titulo: z.string().nullable(),
  etapa: z.string(),
  valor: z.object({ quantia_micros: z.number(), moeda: z.string() }).nullable(),
  version: z.string(),
  fechamento_previsto_em: InstanteSchema.nullable(),
  criada_em: InstanteSchema.nullable(),
  /** Os campos personalizados como ficaram (#568). */
  campos: ValoresDeCamposSchema,
});

export type OportunidadeDaApi = z.infer<typeof OportunidadeSchema>;

/**
 * A nota escrita à mão dentro da oportunidade.
 *
 * ── Por que `autor` traz o NOME, e não só o id ───────────────────────────
 *
 * A tabela de notas é lida por quem não escreveu nenhuma delas — o gestor que
 * assume a carteira, o colega que cobre as férias. Um id ali obrigaria a tela
 * a buscar a pessoa por nota, e o custo cairia justamente na leitura que mais
 * importa: a de quem chegou depois.
 *
 * `autor` é anulável porque a nota sobrevive à saída de quem a escreveu. O
 * texto continua valendo; quem escreveu pode não estar mais aqui.
 */
export const NotaDaOportunidadeSchema = z.object({
  id: IdSchema,
  texto: z.string(),
  autor: z.object({ id: IdSchema, nome: z.string().nullable() }).nullable(),
  criada_em: InstanteSchema,
  /*
   * Nulo é o caso comum: a nota é a original.
   *
   * Preenchido, a tela marca a linha como editada. Sem esta marca, o texto
   * corrigido continuaria PARECENDO registro do que foi dito no dia — e quem
   * abre o card não teria como distinguir o combinado do reescrito.
   */
  editada_em: InstanteSchema.nullable(),
});

export type NotaDaOportunidade = z.infer<typeof NotaDaOportunidadeSchema>;

export const NotasDaOportunidadeSchema = z.object({
  itens: z.array(NotaDaOportunidadeSchema),
});

export type NotasDaOportunidade = z.infer<typeof NotasDaOportunidadeSchema>;

/**
 * O que se manda para escrever uma nota.
 *
 * Só o texto: a nota não tem título, categoria nem tipo. Classificar obrigaria
 * quem escreve a decidir antes de escrever, e o campo vazio de "o que foi
 * combinado" é o que precisa ser fácil de preencher.
 *
 * Sem `version`: nota acrescenta, não altera a oportunidade — não há conflito
 * de edição para detectar. Duas pessoas escrevendo ao mesmo tempo produzem
 * duas notas, que é o resultado certo.
 */
export const CriarNotaDaOportunidadeSchema = z
  .object({ texto: z.string().trim().min(1).max(2000) })
  .strict();

export type CriarNotaDaOportunidade = z.infer<typeof CriarNotaDaOportunidadeSchema>;

/**
 * Corrigir o texto de uma nota já escrita.
 *
 * Só o texto, e sem `version`: a nota tem um campo só, então não existe o caso
 * de duas pessoas mexendo em partes diferentes da mesma linha. Quem salva por
 * último salva — e a trilha guarda antes e depois de cada correção, que é onde
 * se recupera o que havia.
 *
 * Quem pode corrigir é quem pode editar a oportunidade, e não apenas o autor:
 * a correção existe para o gestor arrumar o erro de digitação do vendedor que
 * saiu de férias — o caso em que exigir o autor travaria justamente quem
 * precisa.
 */
export const EditarNotaDaOportunidadeSchema = z
  .object({ texto: z.string().trim().min(1).max(2000) })
  .strict();

export type EditarNotaDaOportunidade = z.infer<typeof EditarNotaDaOportunidadeSchema>;

export type AtribuirOportunidade = z.infer<typeof AtribuirOportunidadeSchema>;
export type AtribuicaoDaOportunidade = z.infer<typeof AtribuicaoDaOportunidadeSchema>;
